import * as React from 'react'
import { ArticleBusinessType, InventoryPool } from 'admin/articleService'
import { useGetAllArticles, useGetAllCategories } from 'products/redux'
import { useGetTimeSlotGroupsQuery } from 'timeSlots/reduxQuery'
import { useGetCapacitiesQuery, useGetProductTimeSlotsQuery } from 'capacity/reduxQuery'
import { useTimeSlotGroupDetails } from 'capacity/hooks/useTimeSlotGroupDetails'
import { useCapacityCapabilities } from 'capacity/hooks/useCapacityAccess'
import { capacityPaths } from 'capacity/paths'
import { FORM_SECTION } from 'capacity/copy'
import { CapacitySectionShell } from './capacitySectionShell'
import { CapacityRow, LinesRow, MessageRow, PendingRow } from './capacitySectionRow'
import { TimeSlotGroupCard } from './timeSlotGroupCard'
import { articleCategoryIds, categoriesAllSwitchedOff, productCapacityRows, productGroupBlocks } from './sectionModel'
import { blocksFromServer } from './serverTimeSlots'

interface ProductCapacitySectionProps {
    accountSlug: string
    productId: string
    businessType: ArticleBusinessType | null
    usedResources: InventoryPool[]
    // True while the form is still fetching the product: usedResources is empty until then.
    loading?: boolean
}

export function ProductCapacitySection({
    accountSlug,
    productId,
    businessType,
    usedResources,
    loading = false,
}: ProductCapacitySectionProps) {
    const isNew = productId === 'new'
    const articles = useGetAllArticles()
    const categories = useGetAllCategories()
    const capacities = useGetCapacitiesQuery(accountSlug, { skip: isNew })
    const serverTimeSlots = useCapacityCapabilities(accountSlug).timeSlots === 'server'
    const groupsQuery = useGetTimeSlotGroupsQuery(accountSlug, { skip: isNew || serverTimeSlots })
    const serverQuery = useGetProductTimeSlotsQuery({ accountSlug, productId }, { skip: isNew || !serverTimeSlots })

    const article = React.useMemo(() => articles.find((entry) => entry.id === productId), [articles, productId])
    const isBundle = !!article?.isBundle || businessType === 'bundle'
    const categoryIds = React.useMemo(() => (article ? articleCategoryIds(article) : []), [article])
    const groups = groupsQuery.data?.groups
    const resolved = React.useMemo(() => {
        if (serverTimeSlots) {
            return serverQuery.data ? blocksFromServer(serverQuery.data, categories) : null
        }
        return groups
            ? { ...productGroupBlocks({ id: productId, categoryIds }, groups, categories), reason: null }
            : null
    }, [serverTimeSlots, serverQuery.data, groups, productId, categoryIds, categories])
    const neededIds = React.useMemo(
        () =>
            !serverTimeSlots && groupsQuery.data && !groupsQuery.data.slotsIncluded && resolved
                ? resolved.blocks.flatMap((b) => b.groups.map((g) => g.id))
                : [],
        [serverTimeSlots, groupsQuery.data, resolved],
    )
    const details = useTimeSlotGroupDetails(accountSlug, neededIds)
    const timeSlotsFailed = serverTimeSlots ? serverQuery.isError : groupsQuery.isError
    const timeSlotsLoaded = serverTimeSlots ? !!serverQuery.data || serverQuery.data === null : !!groups
    const switchedOff = resolved?.reason
        ? resolved.reason === 'categoryDisabled'
        : categoriesAllSwitchedOff(categoryIds, categories)

    const window = resolved ? (resolved.blocks.length > 0 ? 'slot' : 'day') : null
    const rows = productCapacityRows({
        accountSlug,
        productId,
        businessType,
        isBundle,
        usedResources,
        capacities: capacities.data,
        categories,
        window,
    })

    let capacityContent: React.ReactNode
    if (isNew) {
        capacityContent = <MessageRow text={FORM_SECTION.saveProductFirst} />
    } else if (loading || capacities.isLoading) {
        capacityContent = <PendingRow />
    } else if (capacities.isError) {
        capacityContent = <MessageRow text={FORM_SECTION.capacitiesFailed} />
    } else if (rows.length === 0) {
        capacityContent = <MessageRow text={FORM_SECTION.notInAny} />
    } else {
        capacityContent = rows.map((row) => (
            <CapacityRow key={row.key} href={row.href} name={row.name} detail={row.detail} pending={row.pending} />
        ))
    }

    let timeSlotsContent: React.ReactNode
    if (isNew) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.saveProductFirstTimeSlots} />
    } else if (isBundle) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.bundleTimeSlots} />
    } else if (timeSlotsFailed) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.groupsFailed} />
    } else if (!resolved || !timeSlotsLoaded) {
        timeSlotsContent = <PendingRow />
    } else if (resolved.blocks.length === 0) {
        timeSlotsContent = (
            <LinesRow
                lines={[FORM_SECTION.noTimeSlots, switchedOff ? FORM_SECTION.switchedOff : FORM_SECTION.noGroupProduct]}
                linkTo={capacityPaths.timeSlots(accountSlug)}
                linkLabel={FORM_SECTION.goToTimeSlots}
            />
        )
    } else {
        timeSlotsContent = (
            <div className="capacity-section-blocks">
                {resolved.dependsOnCategory && <p className="capacity-section-lead">{FORM_SECTION.viaCategory}</p>}
                {resolved.blocks.map((block) => (
                    <div className="capacity-section-block" key={block.label}>
                        <div className="capacity-section-block-label">{block.label}</div>
                        {block.groups.map((group) => (
                            <TimeSlotGroupCard
                                key={group.id}
                                accountSlug={accountSlug}
                                group={group}
                                detail={details[group.id]}
                            />
                        ))}
                    </div>
                ))}
            </div>
        )
    }

    return <CapacitySectionShell capacities={capacityContent} timeSlots={timeSlotsContent} />
}
